import { toLower } from "lodash";
import { v4 as uuid } from 'uuid';
import { isBooleanFalse, isBooleanTrue, isNotNullOrUndefined, isNumberNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { convertToSlug } from "../utils/stringFormatter";
import { create, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField } from '../utils/dbUtils';
import { db, loadStorage } from "../firebase";
import moment from "moment-timezone";
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";

class ArticleCategory {
    static collectionName = 'articleCategories';

    name = null;
    slug = convertToSlug(this.name);
    parentId = null;
    description = null;
    displayOrder = 0;
    imageRefId = null;
    #image = null;
    hide = false;

    constructor(category) {
        if (isObjectNotEmpty(category)) {
            if (isStringNotEmpty(category.name)) {
                this.name = category.name;
                if (isStringNotEmpty(category.slug)) {
                    this.slug = category.slug;
                } else {
                    this.slug = convertToSlug(category.name);
                }
            }
            if (isStringNotEmpty(category.parentId)) { this.parentId = category.parentId; }
            if (isStringNotEmpty(category.description)) { this.description = category.description; }
            if (isNumberNotEmpty(category.displayOrder)) { this.displayOrder = category.displayOrder; }
            if (isNotNullOrUndefined(category.image)) { this.#image = category.image; }
            if (isStringNotEmpty(category.imageRefId)) { this.imageRefId = category.imageRefId; }
            if (isBooleanTrue(category.hide)) { this.hide = true; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            slug: this.slug,
            parentId: this.parentId,
            description: this.description,
            displayOrder: this.displayOrder,
            imageRefId: this.imageRefId,
            hide: this.hide,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.name)) {
            throw new Error('Category name is required.');
        }

        return true;
    }

    static addFile = async (file) => {
        if (isNotNullOrUndefined(file)) {
            try {
                const fileName = uuid() + '.' + this.getFileExt(file);
                // console.log('fileName', fileName);
                const { storage, ref, uploadBytes } = await loadStorage();
                const storageRef = ref(storage, 'articleCategories/' + fileName);

                return await uploadBytes(storageRef, file);
            } catch (err) {
                console.log(err);
            }
        }

        return null;
    }

    static getFileExt = (file) => {
        const lastDot = file.name.lastIndexOf('.');
        //const fileName = fullFileName.substring(0, lastDot);
        const ext = file.name.substring(lastDot + 1);


        return toLower(ext);
    }

    uploadImage = async () => {
        if (isNotNullOrUndefined(this.#image)) {
            const uploadRes = await ArticleCategory.addFile(this.#image);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                this.imageRefId = uploadRes.ref.fullPath;
            }
        }
    }

    create = async () => {
        if (this.isValid()) {
            await this.uploadImage();

            return await create(ArticleCategory.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(ArticleCategory.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateImage = async (id, file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await this.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                return await this.updateField(id, 'imageRefId', uploadRes.ref.fullPath);
            }
        }

        return false;
    }

    static update = async (id, updatedProductCategory, oldObj) => {
        const updatedObj = new ArticleCategory(updatedProductCategory);
        if (updatedObj.isValid() && id === updatedProductCategory.id) {
            if (isNotNullOrUndefined(updatedObj.#image)) {
                const uploadRes = await ArticleCategory.addFile(updatedObj.#image);
                // console.log(uploadRes);
                if (isObjectNotEmpty(uploadRes)) {
                    updatedObj.imageRefId = uploadRes.ref.fullPath;
                }
            }

            let success = await update(ArticleCategory.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj.toDto() };
            }
        } else {
            throw new Error('id and id field of the updated product category must match.')
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(ArticleCategory.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(ArticleCategory.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(ArticleCategory.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(ArticleCategory.collectionName, fieldName, fieldValue, comparer);
    }

    static getChildProductCategoriesByParentId = async (parentId) => {
        return await getBySingleQuery(ArticleCategory.collectionName, 'parentId', parentId, '==');
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(ArticleCategory.collectionName, ids, 'in');
    }

    static getCount = async (queryParameters) => {
        const { hide } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(hide)) {
            queryConstraints.push(where('hide', '==', true));
        } else if (isBooleanFalse(hide)) {
            queryConstraints.push(where('hide', '==', false));
        }

        let q = query(collection(db, ArticleCategory.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'displayOrder') => {
        const { hide } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(hide)) {
            queryConstraints.push(where('hide', '==', true));
        } else if (isBooleanFalse(hide)) {
            queryConstraints.push(where('hide', '==', false));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, ArticleCategory.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, ArticleCategory.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, ArticleCategory.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await this.getPaginated(queryParameters, 10000, null, next, 'displayOrder');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default ArticleCategory;